import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ChevronRight, LockKeyhole } from "lucide-react";
import DemoLoop from "@/components/DemoLoop";

/**
 * GuestPreview — pattern réutilisable pour les pages premium.
 * Affiche un kicker, titre, description, demo loop animée, contenu d'exemple
 * (children), puis un gros CTA d'inscription.
 *
 * Props :
 *   - kicker        : string (ex: "Taste · Suggestions")
 *   - kickerIcon    : ReactNode (icône Lucide)
 *   - title         : string
 *   - description   : string|ReactNode
 *   - demoSteps     : tableau d'étapes pour DemoLoop ({ title, body, icon, color })
 *   - nextPath      : string (ex: "/taste/suggestions") — redirect après inscription
 *   - ctaTitle      : string (gros titre du CTA en bas)
 *   - ctaSubtitle   : string (sous-titre du CTA)
 *   - children      : zone d'exemple visuel (cartes, sliders, etc.)
 *   - testId        : string (data-testid racine)
 */
export default function GuestPreview({
  kicker, kickerIcon: KickerIcon, title, description,
  demoSteps = [], nextPath, ctaTitle, ctaSubtitle,
  children, testId = "guest-preview",
}) {
  return (
    <div className={`max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-16`} data-testid={testId}>
      <div className="flex items-center gap-4 mb-5">
        <img
          src="/brand/audrey-ai.png"
          alt="geoVINUM AI"
          className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-4 ring-[#FCE4ED] shadow-md shrink-0"
        />
        <div>
          <span className="chip-pink inline-flex items-center gap-1.5">
            {KickerIcon ? <KickerIcon className="w-3 h-3" /> : null}
            {kicker}
          </span>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8B95A1] mt-1 font-bold">geoVINUM AI</p>
        </div>
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-1 leading-[1.05]">{title}</h1>
      {description && <p className="text-[#4A5568] mt-3 max-w-2xl">{description}</p>}

      {demoSteps.length > 0 && (
        <div className="mt-7"><DemoLoop steps={demoSteps} testId={`${testId}-demo`} /></div>
      )}

      {/* Zone d'exemple */}
      {children && (
        <div className="mt-8 relative">
          <div className="absolute -top-3 left-5 z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1F3A5F] text-white text-[10px] font-bold uppercase tracking-widest">
            <Sparkles className="w-3 h-3 text-[#E5276A]" /> Exemple
          </div>
          <div className="border-2 border-dashed border-[#E5276A]/30 rounded-3xl p-5 md:p-6 bg-white/60 backdrop-blur-sm">
            {children}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="mt-10 rounded-3xl border-2 border-[#E5276A] bg-gradient-to-br from-[#FCE4ED]/60 via-white to-[#FBF7F3] p-6 md:p-8 relative overflow-hidden">
        <LockKeyhole className="absolute -top-6 -right-6 w-32 h-32 text-[#E5276A]/8" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E5276A] text-white text-[10px] font-bold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" /> Gratuit · sans CB
          </span>
          <h2 className="font-display text-2xl md:text-3xl text-[#1F3A5F] mt-3 leading-tight">{ctaTitle}</h2>
          {ctaSubtitle && <p className="text-[#4A5568] mt-2 max-w-xl text-sm md:text-base">{ctaSubtitle}</p>}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link to={`/register?next=${encodeURIComponent(nextPath)}`} data-testid={`${testId}-cta-register`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] transition-colors">
              <Sparkles className="w-4 h-4" /> Créer mon compte gratuit
            </Link>
            <Link to={`/login?next=${encodeURIComponent(nextPath)}`} data-testid={`${testId}-cta-login`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-[#1F3A5F]/20 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A] transition-colors">
              J'ai déjà un compte
            </Link>
          </div>
          <p className="text-xs text-[#8B95A1] mt-4 inline-flex items-center gap-1.5">
            <ChevronRight className="w-3 h-3 text-[#E5276A]" /> Crédit offert dès l'inscription pour tester
          </p>
        </div>
      </div>
    </div>
  );
}
